/**
 * Zentrale Fehlermeldungen für die Anwendung
 *
 * Diese Datei enthält alle benutzerfreundlichen Fehlermeldungen, die in der Anwendung
 * verwendet werden. Die Zentralisierung ermöglicht:
 * - Einheitliche Formulierung
 * - Einfache Übersetzung/Lokalisierung via $localize
 * - Konsistente Fehlerbehandlung
 *
 * WICHTIG: Diese Nachrichten werden zur Laufzeit verwendet und sollten
 * benutzerfreundlich formuliert sein. Alle User-Facing Strings sind mit
 * $localize markiert für i18n-Unterstützung.
 */

/**
 * SSE-Streaming Fehlermeldungen
 */
export const SSE_ERROR_MESSAGES = {
  /**
   * Timeout-Fehler wenn eine Anfrage zu lange dauert
   */
  TIMEOUT: $localize`:@@error.sse.timeout:Die Anfrage hat zu lange gedauert (5 Minuten). Bitte versuchen Sie es erneut.`,

  /**
   * Netzwerkfehler bei Verbindungsproblemen
   */
  NETWORK: $localize`:@@error.sse.network:Netzwerkfehler: Verbindung zum Server verloren. Bitte überprüfen Sie Ihre Internetverbindung.`,

  /**
   * HTTP-Serverfehler beim Streaming
   * @param status HTTP-Statuscode
   */
  HTTP_ERROR: (status: number) =>
    $localize`:@@error.sse.http:HTTP ${status}:STATUS:: Server-Fehler beim Streamen der Nachricht. Bitte versuchen Sie es erneut.`,

  /**
   * Unbekannter Fehler als Fallback
   */
  UNKNOWN: $localize`:@@error.sse.unknown:Unbekannter Fehler beim Streamen der Nachricht.`,

  /**
   * Browser liefert keinen Response-Body-Reader (Streaming nicht möglich)
   */
  NO_BODY_READER: $localize`:@@error.sse.noBodyReader:Die Antwort des Servers konnte nicht gelesen werden. Bitte versuchen Sie es erneut.`,

  /**
   * SSE-Nachricht ohne data-Feld (Protokollfehler)
   */
  PARSE_NO_DATA: $localize`:@@error.sse.parseNoData:Die Antwort des Servers war unvollständig. Bitte versuchen Sie es erneut.`,
} as const;

/**
 * Datums-Validierungs Fehlermeldungen
 */
export const DATE_ERROR_MESSAGES = {
  /**
   * Ungültiger Eingabetyp (weder String noch Date)
   */
  INVALID_INPUT: $localize`:@@error.date.invalidInput:Ungültige Eingabe: Ein String oder Date-Objekt wird erwartet.`,

  /**
   * Ungültiger Datumsstring
   */
  INVALID_DATE_STRING: $localize`:@@error.date.invalidString:Ungültiger Datumsstring.`,
} as const;

/**
 * Portal-Integration Fehlermeldungen
 */
export const PORTAL_ERROR_MESSAGES = {
  /**
   * Anwendung läuft nicht im Portal
   */
  NOT_IN_PORTAL: $localize`:@@error.portal.notInPortal:Die Anwendung läuft nicht im Portal.`,

  /**
   * PortalMessageBroker nicht initialisiert
   */
  NOT_INITIALIZED: $localize`:@@error.portal.notInitialized:PortalMessageBroker ist nicht initialisiert. Bitte init() aufrufen.`,

  /**
   * Intent-ID darf nicht leer sein
   */
  EMPTY_INTENT_ID: $localize`:@@error.portal.emptyIntentId:Intent-ID darf nicht leer sein.`,

  /**
   * Intent-Version darf nicht leer sein
   */
  EMPTY_INTENT_VERSION: $localize`:@@error.portal.emptyIntentVersion:Intent-Version darf nicht leer sein.`,

  /**
   * Callback bereits registriert
   * @param intentId Intent-ID
   * @param intentVersion Intent-Version
   */
  CALLBACK_EXISTS: (intentId: string, intentVersion: string) =>
    $localize`:@@error.portal.callbackExists:Es existiert bereits ein Callback für ${intentId}:INTENT_ID: mit Version ${intentVersion}:INTENT_VERSION:.`,
} as const;

/**
 * HTTP-Fehlermeldungen für ErrorHandlerService
 */
export const HTTP_ERROR_MESSAGES = {
  /**
   * Service Unavailable (503) - Wartungsmodus
   */
  SERVICE_UNAVAILABLE: $localize`:@@error.http.serviceUnavailable:Der Chatbot wird gerade aktualisiert und ist in Kürze wieder verfügbar.`,
} as const;

/**
 * UI-Texte für Conversation-Liste
 */
export const CONVERSATION_UI_MESSAGES = {
  /**
   * Standardtitel für neue Unterhaltungen
   */
  NEW_CHAT_TITLE: $localize`:@@conversation.newChatTitle:Neuer Chat`,

  /**
   * Fallback-Titel wenn Unterhaltung keinen Titel hat
   */
  UNTITLED: $localize`:@@conversation.untitled:Unbenannt`,

} as const;
